// Two Pointers — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Two Pointers Explanation modal. Same shape as arraysDrills.js/hashingDrills.js: concise
// steps + an interactive "watch it happen" visualizer per version.

const twoPointersDrills = [
  {
    id: 'reverse-array-two-pointers',
    title: 'Reverse an Array Using Two Pointers',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Build a New Reversed Array',
        explanation: 'Walk the original array back to front, copying each value into a brand-new array. Correct and easy to reason about, but it pays for a whole second array just to hold the answer.',
        steps: [
          'Create a new array of the same length.',
          'Walk the original array from the last index to the first.',
          'Copy each value into the new array in that order.',
          'Return the new array — the original is left untouched.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [10, 20, 30, 40] }, { label: 'result', values: [null, null, null, null] }],
          varNames: [],
          steps: [
            { highlights: { arr: [3] }, snapshots: { result: [40, null, null, null] }, note: 'arr[3]=40 is copied first — it becomes result[0].' },
            { highlights: { arr: [2] }, snapshots: { result: [40, 30, null, null] }, note: 'arr[2]=30 → result[1].' },
            { highlights: { arr: [1] }, snapshots: { result: [40, 30, 20, null] }, note: 'arr[1]=20 → result[2].' },
            { highlights: { arr: [0] }, snapshots: { result: [40, 30, 20, 10] }, note: 'arr[0]=10 → result[3]. New array complete — original arr never changed.' },
          ],
        },
        code:
`public int[] reverseArrayBasic(int[] arr) {
    int[] result = new int[arr.length];
    int j = 0;
    for (int i = arr.length - 1; i >= 0; i--) {
        result[j] = arr[i];
        j++;
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — a second array' },
      },
      {
        name: 'Optimized — Swap In Place With Two Converging Pointers',
        explanation: 'Put a pointer at each end and swap the values they point to, then move both pointers one step inward. They meet in the middle having touched every element exactly once — no extra array needed.',
        steps: [
          'Set left = 0 and right = arr.length − 1.',
          'While left < right: swap arr[left] and arr[right].',
          'Move left forward by one and right backward by one.',
          'Stop when left and right meet or cross — the array is reversed in place.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [10, 20, 30, 40] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [3] } }, note: 'left=0, right=3. Swap arr[0] and arr[3].' },
            { snapshots: { arr: [40, 20, 30, 10] }, highlights: { arr: { primary: [1], secondary: [2] } }, note: 'After the swap: [40, 20, 30, 10]. left=1, right=2 now — swap arr[1] and arr[2] next.' },
            { snapshots: { arr: [40, 30, 20, 10] }, note: 'After the second swap: [40, 30, 20, 10]. left advances to 2, right drops to 1 — left is no longer less than right, so the reversal is complete.' },
          ],
        },
        code:
`public void reverseArrayInPlace(int[] arr) {
    int left = 0, right = arr.length - 1;
    while (left < right) {
        int temp = arr[left];
        arr[left] = arr[right];
        arr[right] = temp;
        left++;
        right--;
    }
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'check-palindrome-two-pointers',
    title: 'Check if a String Is a Palindrome',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Reverse and Compare',
        explanation: 'Build a fully reversed copy of the string, then compare it to the original. If they\'re identical, it\'s a palindrome. Simple, but builds an entire second string even when the very first characters already disagree.',
        steps: [
          'Build a new string by reading the original back to front.',
          'Compare the reversed string to the original with .equals().',
          'If they match exactly, the string is a palindrome.',
          'The full reversed copy is built even if a mismatch was obvious from character one.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['l', 'e', 'v', 'e', 'l'] }, { label: 'reversed', values: [null, null, null, null, null] }],
          varNames: [],
          steps: [
            { highlights: { s: [4] }, snapshots: { reversed: ['l', null, null, null, null] }, note: "s[4]='l' → reversed[0]." },
            { highlights: { s: [3] }, snapshots: { reversed: ['l', 'e', null, null, null] }, note: "s[3]='e' → reversed[1]." },
            { highlights: { s: [2] }, snapshots: { reversed: ['l', 'e', 'v', null, null] }, note: "s[2]='v' → reversed[2]." },
            { highlights: { s: [1] }, snapshots: { reversed: ['l', 'e', 'v', 'e', null] }, note: "s[1]='e' → reversed[3]." },
            { highlights: { s: [0] }, snapshots: { reversed: ['l', 'e', 'v', 'e', 'l'] }, note: '"level" reversed is "level" — identical → palindrome!' },
          ],
        },
        code:
`public boolean isPalindromeBasic(String s) {
    String reversed = new StringBuilder(s).reverse().toString();
    return s.equals(reversed);
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the reversed copy' },
      },
      {
        name: 'Optimized — Two Pointers Converging, Compare and Stop Early',
        explanation: 'Compare the outermost characters directly, moving both pointers inward one pair at a time. The moment two characters disagree, stop immediately — no need to build anything, and no need to check the rest of the string.',
        steps: [
          'Set left = 0 and right = s.length() − 1.',
          'While left < right, compare s.charAt(left) with s.charAt(right).',
          'If they differ, return false immediately — no more comparisons needed.',
          'If they match, move left forward and right backward and repeat until they meet.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['l', 'e', 'v', 'e', 'l'] }],
          varNames: [],
          steps: [
            { highlights: { s: { primary: [0], secondary: [4] } }, note: "s[0]='l' equals s[4]='l' — move both pointers inward." },
            { highlights: { s: { primary: [1], secondary: [3] } }, note: "s[1]='e' equals s[3]='e' — move both pointers inward." },
            { highlights: { s: { primary: [2], secondary: [2] } }, note: 'left (2) meets right (2) — every pair matched. Palindrome confirmed, and only 2 comparisons were needed.' },
          ],
        },
        code:
`public boolean isPalindrome(String s) {
    int left = 0, right = s.length() - 1;
    while (left < right) {
        if (s.charAt(left) != s.charAt(right)) {
            return false;
        }
        left++;
        right--;
    }
    return true;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'pair-sum-sorted',
    title: 'Find a Pair With Given Sum in a Sorted Array',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Check Every Pair',
        explanation: 'Compare every element to every later element until a pair sums to the target. Correct, but never takes advantage of the fact the array is already sorted.',
        steps: [
          'For each index i, check every later index j.',
          'If arr[i] + arr[j] equals target, return that pair.',
          'Every pair is checked in the worst case — O(n²), regardless of the array being sorted.',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 9)', values: [1, 2, 4, 5, 7] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [1] } }, note: '1 + 2 = 3. No.' },
            { highlights: { arr: { primary: [0], secondary: [2] } }, note: '1 + 4 = 5. No.' },
            { highlights: { arr: { primary: [0], secondary: [3] } }, note: '1 + 5 = 6. No.' },
            { highlights: { arr: { primary: [0], secondary: [4] } }, note: '1 + 7 = 8. No.' },
            { highlights: { arr: { primary: [1], secondary: [2] } }, note: '2 + 4 = 6. No.' },
            { highlights: { arr: { primary: [1], secondary: [3] } }, note: '2 + 5 = 7. No.' },
            { highlights: { arr: { primary: [1], secondary: [4] } }, note: '2 + 7 = 9. Match! Pair: (2, 7).' },
          ],
        },
        code:
`public int[] pairWithSumBasic(int[] arr, int target) {
    for (int i = 0; i < arr.length; i++) {
        for (int j = i + 1; j < arr.length; j++) {
            if (arr[i] + arr[j] == target) {
                return new int[] { arr[i], arr[j] };
            }
        }
    }
    return new int[] {};
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Two Pointers From Both Ends',
        explanation: 'Because the array is sorted, the sum of the outermost pair only moves in one predictable direction as each pointer shifts — too small means move left forward, too big means move right backward. That structure is what lets one pass replace all n² comparisons.',
        steps: [
          'Set left = 0 and right = arr.length − 1.',
          'If arr[left] + arr[right] equals target, that\'s the pair.',
          'If the sum is too small, move left forward (the only way to increase the sum).',
          'If the sum is too big, move right backward (the only way to decrease the sum).',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 9)', values: [1, 2, 4, 5, 7] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [4] } }, note: '1 + 7 = 8, too small — move left forward.' },
            { highlights: { arr: { primary: [1], secondary: [4] } }, note: '2 + 7 = 9 — match! Pair: (2, 7). Found in 2 comparisons instead of scanning every pair.' },
          ],
        },
        code:
`public int[] pairWithSum(int[] arr, int target) {
    int left = 0, right = arr.length - 1;
    while (left < right) {
        int sum = arr[left] + arr[right];
        if (sum == target) {
            return new int[] { arr[left], arr[right] };
        } else if (sum < target) {
            left++;
        } else {
            right--;
        }
    }
    return new int[] {};
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'segregate-even-odd',
    title: 'Segregate Even and Odd Numbers (Partition)',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Two Separate Lists, Then Combine',
        explanation: 'Walk the array once, sorting each value into an "evens" list or an "odds" list, then join the two lists back together. Correct, but pays for two extra lists just to reorder one array.',
        steps: [
          'Create an empty list for evens and one for odds.',
          'Walk the array once, appending each value to the correct list.',
          'Concatenate the evens list followed by the odds list.',
          'Two extra lists are allocated just to rearrange values that were already there.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [3, 8, 5, 2, 7, 4] }, { label: 'evens', values: [] }, { label: 'odds', values: [] }],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, snapshots: { odds: [3] }, note: '3 is odd → append to odds.' },
            { highlights: { arr: [1] }, snapshots: { evens: [8] }, note: '8 is even → append to evens.' },
            { highlights: { arr: [2] }, snapshots: { odds: [3, 5] }, note: '5 is odd → append to odds.' },
            { highlights: { arr: [3] }, snapshots: { evens: [8, 2] }, note: '2 is even → append to evens.' },
            { highlights: { arr: [4] }, snapshots: { odds: [3, 5, 7] }, note: '7 is odd → append to odds.' },
            { highlights: { arr: [5] }, snapshots: { evens: [8, 2, 4] }, note: '4 is even → append to evens. Final: evens + odds = [8, 2, 4, 3, 5, 7].' },
          ],
        },
        code:
`public int[] segregateBasic(int[] arr) {
    List<Integer> evens = new ArrayList<>();
    List<Integer> odds = new ArrayList<>();
    for (int num : arr) {
        if (num % 2 == 0) evens.add(num); else odds.add(num);
    }
    int[] result = new int[arr.length];
    int idx = 0;
    for (int e : evens) result[idx++] = e;
    for (int o : odds) result[idx++] = o;
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — two extra lists' },
      },
      {
        name: 'Optimized — Two Pointers From Both Ends, In Place',
        explanation: 'Keep left scanning forward for an out-of-place odd number and right scanning backward for an out-of-place even number, then swap the two. No extra lists — every swap fixes exactly two elements at once.',
        steps: [
          'Set left = 0 and right = arr.length − 1.',
          'Move left forward while arr[left] is already even.',
          'Move right backward while arr[right] is already odd.',
          'When left finds an odd and right finds an even (with left < right), swap them and continue.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [3, 8, 5, 2, 7, 4] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [5] } }, note: 'left=0: arr[0]=3 is already odd — stop immediately. right=5: arr[5]=4 is already even — stop immediately. Swap them.' },
            { snapshots: { arr: [4, 8, 5, 2, 7, 3] }, highlights: { arr: { primary: [2], secondary: [3] } }, note: 'After the swap: [4, 8, 5, 2, 7, 3]. left skips past 8 (even) to index 2 (5, odd). right skips past 7 (odd) to index 3 (2, even). Swap those two next.' },
            { snapshots: { arr: [4, 8, 2, 5, 7, 3] }, note: 'After swapping index 2 and 3: [4, 8, 2, 5, 7, 3]. left advances to 3, right drops to 2 — left is no longer less than right, so the scan stops. Evens [4, 8, 2] now sit before odds [5, 7, 3].' },
          ],
        },
        code:
`public void segregate(int[] arr) {
    int left = 0, right = arr.length - 1;
    while (left < right) {
        while (left < right && arr[left] % 2 == 0) left++;
        while (left < right && arr[right] % 2 != 0) right--;
        if (left < right) {
            int temp = arr[left];
            arr[left] = arr[right];
            arr[right] = temp;
            left++;
            right--;
        }
    }
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'remove-duplicates-sorted',
    title: 'Remove Duplicates From a Sorted Array In-Place',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Filter Into a New Array With a HashSet',
        explanation: 'Walk the array, only keeping a value the first time a HashSet confirms it hasn\'t been seen yet, then copy the survivors into a new array. Correct, but a HashSet is unnecessary overhead when the array is already sorted (duplicates are always neighbors).',
        steps: [
          'Create an empty HashSet and an empty result list.',
          'Walk the array once, adding a value to the result only if the set doesn\'t already contain it.',
          'Convert the result list back into an array.',
          'The HashSet pays for hashing every value even though sorted duplicates are always adjacent.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [1, 1, 2, 2, 2, 3] }, { label: 'result', values: [] }],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, snapshots: { result: [1] }, note: '1 not seen yet → keep. result = [1].' },
            { highlights: { arr: [1] }, note: '1 already seen (in the set) → skip.' },
            { highlights: { arr: [2] }, snapshots: { result: [1, 2] }, note: '2 not seen yet → keep. result = [1, 2].' },
            { highlights: { arr: [3] }, note: '2 already seen → skip.' },
            { highlights: { arr: [4] }, note: '2 already seen → skip.' },
            { highlights: { arr: [5] }, snapshots: { result: [1, 2, 3] }, note: '3 not seen yet → keep. Final result: [1, 2, 3].' },
          ],
        },
        code:
`public int[] removeDuplicatesBasic(int[] arr) {
    Set<Integer> seen = new HashSet<>();
    List<Integer> result = new ArrayList<>();
    for (int num : arr) {
        if (seen.add(num)) {
            result.add(num);
        }
    }
    return result.stream().mapToInt(Integer::intValue).toArray();
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the set and result list' },
      },
      {
        name: 'Optimized — Same-Direction Slow/Fast Pointers, In Place',
        explanation: 'slow marks the last position of the unique prefix built so far. fast scans ahead looking for the next value that differs from arr[slow]; whenever it finds one, slow advances and copies it in. No extra memory — the array rearranges itself.',
        steps: [
          'Set slow = 0 (the first element is always unique so far).',
          'Move fast from index 1 to the end.',
          'Whenever arr[fast] differs from arr[slow], advance slow and copy arr[fast] into arr[slow].',
          'When fast finishes, slow + 1 is the count of unique elements, all sitting at the front.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [1, 1, 2, 2, 2, 3] }],
          varNames: ['slow'],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [1] } }, vars: { slow: 0 }, note: 'slow=0, fast=1: arr[1]=1 equals arr[slow]=1 — duplicate, fast keeps moving.' },
            { highlights: { arr: { primary: [0], secondary: [2] } }, vars: { slow: 1 }, snapshots: { arr: [1, 2, 2, 2, 2, 3] }, note: 'fast=2: arr[2]=2 differs from arr[slow]=1 — slow advances to 1, arr[1] becomes 2.' },
            { highlights: { arr: { primary: [1], secondary: [3] } }, vars: { slow: 1 }, note: 'fast=3: arr[3]=2 equals arr[slow]=2 — duplicate, skip.' },
            { highlights: { arr: { primary: [1], secondary: [4] } }, vars: { slow: 1 }, note: 'fast=4: arr[4]=2 equals arr[slow]=2 — duplicate, skip.' },
            { highlights: { arr: { primary: [1], secondary: [5] } }, vars: { slow: 2 }, snapshots: { arr: [1, 2, 3, 2, 2, 3] }, note: 'fast=5: arr[5]=3 differs — slow advances to 2, arr[2] becomes 3. Unique prefix: [1, 2, 3] (slow+1 = 3 elements).' },
          ],
        },
        code:
`public int removeDuplicates(int[] arr) {
    if (arr.length == 0) return 0;
    int slow = 0;
    for (int fast = 1; fast < arr.length; fast++) {
        if (arr[fast] != arr[slow]) {
            slow++;
            arr[slow] = arr[fast];
        }
    }
    return slow + 1; // number of unique elements, now at the front of arr
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'find-middle-fast-slow',
    title: 'Find the Middle Element Using Fast & Slow Pointers',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Use the Known Length',
        explanation: 'Since an array\'s length is known upfront, the middle index is just length / 2 — a direct O(1) lookup. This works fine for arrays, but the same shortcut doesn\'t exist for a singly linked list, where length isn\'t known without a separate pass.',
        steps: [
          'Read arr.length directly.',
          'Compute mid = arr.length / 2.',
          'Return arr[mid].',
          'This only works because arrays expose their length instantly — it\'s not the general technique.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [4, 8, 15, 16, 23, 42, 8] }],
          varNames: [],
          steps: [
            { highlights: { arr: [3] }, note: 'length = 7, mid = 7 / 2 = 3. arr[3] = 16 is the middle — read directly, no scanning at all.' },
          ],
        },
        code:
`public int findMiddleBasic(int[] arr) {
    int mid = arr.length / 2;
    return arr[mid];
}`,
        complexity: { time: 'O(1)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Fast & Slow Pointers (Generalizes Beyond Arrays)',
        explanation: 'Move slow one step and fast two steps at the same time. When fast runs out of room, slow is standing exactly on the middle — because fast always covers exactly twice the distance slow does. This is the technique worth memorizing: it works even when you can\'t ask for a length upfront, like walking a singly linked list node by node.',
        steps: [
          'Start both slow and fast at index 0.',
          'On each step, move slow forward by 1 and fast forward by 2.',
          'Stop when fast reaches or passes the last index.',
          'slow is now sitting on the middle element.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [4, 8, 15, 16, 23, 42, 8] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [0] } }, note: 'slow=0, fast=0 — start.' },
            { highlights: { arr: { primary: [1], secondary: [2] } }, note: 'slow moves to 1 (+1), fast moves to 2 (+2).' },
            { highlights: { arr: { primary: [2], secondary: [4] } }, note: 'slow moves to 2, fast moves to 4.' },
            { highlights: { arr: { primary: [3], secondary: [6] } }, note: 'slow moves to 3, fast moves to 6 — the last index. Stop: fast can\'t move +2 without leaving the array.' },
            { highlights: { arr: { primary: [3] } }, note: 'slow=3 is exactly the middle — same answer as the length/2 shortcut, but reached without ever asking for the array\'s length.' },
          ],
        },
        code:
`public int findMiddle(int[] arr) {
    int slow = 0, fast = 0;
    while (fast < arr.length - 1) {
        slow++;
        fast += 2;
        if (fast > arr.length - 1) fast = arr.length - 1;
    }
    return arr[slow];
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'three-sum-drill',
    title: 'Find a Triplet With a Given Sum (3Sum Foundation)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Check Every Triplet',
        explanation: 'Try every combination of three indices and check their sum. Direct, but three nested loops mean the work grows as fast as n cubed.',
        steps: [
          'For each i, for each j > i, for each k > j: check arr[i] + arr[j] + arr[k].',
          'If the sum equals target, record the triplet.',
          'Every combination of 3 indices gets checked — O(n³).',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 15)', values: [2, 5, 6, 8, 9] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0, 1], secondary: [2] } }, note: '2 + 5 + 6 = 13. No.' },
            { highlights: { arr: { primary: [0, 1], secondary: [3] } }, note: '2 + 5 + 8 = 15. Match! Triplet: (2, 5, 8). A brute force scan keeps checking every remaining combination too.' },
          ],
        },
        code:
`public int[] threeSumBruteForce(int[] arr, int target) {
    for (int i = 0; i < arr.length; i++) {
        for (int j = i + 1; j < arr.length; j++) {
            for (int k = j + 1; k < arr.length; k++) {
                if (arr[i] + arr[j] + arr[k] == target) {
                    return new int[] { arr[i], arr[j], arr[k] };
                }
            }
        }
    }
    return new int[] {};
}`,
        complexity: { time: 'O(n³)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Sort, Fix One Element, Two-Pointer the Rest',
        explanation: 'Sort the array, then fix the smallest element of the triplet at index i and run the same two-pointer pair-sum technique on the remaining slice to find the other two. One O(n) two-pointer sweep per fixed i turns the cubic search into a quadratic one.',
        steps: [
          'Sort the array first.',
          'For each index i, treat arr[i] as fixed and set target2 = target − arr[i].',
          'Run two pointers (left = i+1, right = end) across the rest of the array looking for a pair that sums to target2.',
          'This reuses the exact two-pointer pair-sum idea inside an outer loop — O(n) work per i, O(n²) total.',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 15)', values: [2, 5, 6, 8, 9] }],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, note: 'Fix i=0 (value 2). Need a pair summing to 15 − 2 = 13 in the rest of the array.' },
            { highlights: { arr: { primary: [1], secondary: [4] } }, note: 'left=1 (5), right=4 (9): 5+9=14, too big — move right backward.' },
            { highlights: { arr: { primary: [1], secondary: [3] } }, note: 'left=1 (5), right=3 (8): 5+8=13 — match! Triplet: (2, 5, 8), found with one two-pointer sweep instead of two nested loops.' },
          ],
        },
        code:
`public int[] threeSum(int[] arr, int target) {
    int[] sorted = Arrays.copyOf(arr, arr.length);
    Arrays.sort(sorted);

    for (int i = 0; i < sorted.length - 2; i++) {
        int left = i + 1, right = sorted.length - 1;
        int target2 = target - sorted[i];
        while (left < right) {
            int sum = sorted[left] + sorted[right];
            if (sum == target2) {
                return new int[] { sorted[i], sorted[left], sorted[right] };
            } else if (sum < target2) {
                left++;
            } else {
                right--;
            }
        }
    }
    return new int[] {};
}`,
        complexity: { time: 'O(n²)', space: 'O(n) — for the sorted copy' },
      },
    ],
  },
  {
    id: 'sort-colors',
    title: 'Sort an Array of 0s, 1s, and 2s (Dutch National Flag)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Counting Sort in Two Passes',
        explanation: 'Since there are only three possible values, count how many 0s, 1s, and 2s exist, then overwrite the array with that many of each in order. Correct and O(n), but it needs two full passes over the array.',
        steps: [
          'Pass 1: count how many 0s, 1s, and 2s appear.',
          'Pass 2: overwrite the array — first the 0s, then the 1s, then the 2s, using the counts.',
          'Two full passes are needed: one to count, one to rewrite.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [2, 0, 1, 2, 1, 0] }],
          varNames: ['count0', 'count1', 'count2'],
          steps: [
            { vars: { count0: 2, count1: 2, count2: 2 }, note: 'Pass 1 (counting): two 0s, two 1s, two 2s.' },
            { snapshots: { arr: [0, 0, 1, 1, 2, 2] }, note: 'Pass 2 (overwrite): write 0,0 then 1,1 then 2,2. Sorted — but two full passes were needed.' },
          ],
        },
        code:
`public void sortColorsCounting(int[] arr) {
    int count0 = 0, count1 = 0, count2 = 0;
    for (int num : arr) {
        if (num == 0) count0++;
        else if (num == 1) count1++;
        else count2++;
    }
    int idx = 0;
    for (int i = 0; i < count0; i++) arr[idx++] = 0;
    for (int i = 0; i < count1; i++) arr[idx++] = 1;
    for (int i = 0; i < count2; i++) arr[idx++] = 2;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Three-Pointer Dutch National Flag Partition',
        explanation: 'Keep three regions in place as you scan once: low marks the end of the 0s region, high marks the start of the 2s region, and mid scans forward, swapping the current value into the correct region the moment it\'s seen. One pass, no counting step needed first.',
        steps: [
          'Set low = 0, mid = 0, high = arr.length − 1.',
          'If arr[mid] is 0, swap it with arr[low], advance both low and mid.',
          'If arr[mid] is 1, it\'s already in the right region — just advance mid.',
          'If arr[mid] is 2, swap it with arr[high] and shrink high — but do NOT advance mid yet, since the swapped-in value still needs to be checked.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [2, 0, 1, 2, 1, 0] }],
          varNames: ['low', 'mid', 'high'],
          steps: [
            { highlights: { arr: [0] }, vars: { low: 0, mid: 0, high: 5 }, note: 'arr[mid]=2 → swap with arr[high]. high shrinks; mid stays.' },
            { snapshots: { arr: [0, 0, 1, 2, 1, 2] }, highlights: { arr: [0] }, vars: { low: 0, mid: 0, high: 4 }, note: 'Now arr[mid]=0 → swap with arr[low]. Both low and mid advance.' },
            { snapshots: { arr: [0, 0, 1, 2, 1, 2] }, highlights: { arr: [1] }, vars: { low: 1, mid: 1, high: 4 }, note: 'arr[mid]=0 again → swap with arr[low] (same spot). low and mid both advance to 2.' },
            { highlights: { arr: [2] }, vars: { low: 2, mid: 2, high: 4 }, note: 'arr[mid]=1 → already correct region, just advance mid to 3.' },
            { highlights: { arr: [3] }, vars: { low: 2, mid: 3, high: 4 }, note: 'arr[mid]=2 → swap with arr[high], high shrinks to 3. mid stays at 3 to recheck the swapped-in value.' },
            { snapshots: { arr: [0, 0, 1, 1, 2, 2] }, vars: { low: 2, mid: 3, high: 3 }, note: 'Now arr[mid]=1 → advance mid to 4. mid > high — done. Final: [0, 0, 1, 1, 2, 2], sorted in one pass.' },
          ],
        },
        code:
`public void sortColors(int[] arr) {
    int low = 0, mid = 0, high = arr.length - 1;
    while (mid <= high) {
        if (arr[mid] == 0) {
            swap(arr, low, mid);
            low++; mid++;
        } else if (arr[mid] == 1) {
            mid++;
        } else {
            swap(arr, mid, high);
            high--; // don't advance mid — recheck the swapped-in value
        }
    }
}

private void swap(int[] arr, int i, int j) {
    int temp = arr[i]; arr[i] = arr[j]; arr[j] = temp;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'squares-sorted-array',
    title: 'Squares of a Sorted Array',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Square Everything, Then Sort',
        explanation: 'Square every element (negatives become positive), then sort the whole result. Correct, but sorting throws away the fact that the input was already sorted — just not by absolute value.',
        steps: [
          'Create a result array of the same length.',
          'Square every element from the input into the result array.',
          'Sort the result array.',
          'The sort ignores that the input was already ordered — just not by magnitude.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [-4, -1, 0, 3, 10] }, { label: 'squares', values: [16, 1, 0, 9, 100] }],
          varNames: [],
          steps: [
            { note: 'Square every value: [-4,-1,0,3,10] → [16, 1, 0, 9, 100].' },
            { snapshots: { squares: [0, 1, 9, 16, 100] }, note: 'Sort the squares: [0, 1, 9, 16, 100]. Correct, but needed a full O(n log n) sort.' },
          ],
        },
        code:
`public int[] sortedSquaresBasic(int[] arr) {
    int[] result = new int[arr.length];
    for (int i = 0; i < arr.length; i++) {
        result[i] = arr[i] * arr[i];
    }
    Arrays.sort(result);
    return result;
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Two Pointers From Both Ends, Fill Back to Front',
        explanation: 'In a sorted array, the LARGEST square always belongs to whichever end (leftmost negative or rightmost positive) is farther from zero. Compare both ends, place the bigger square at the back of the result, and step that pointer inward — repeating this always places values in the correct final order.',
        steps: [
          'Set left = 0, right = arr.length − 1, and a write pointer at the end of the result array.',
          'Compare |arr[left]| and |arr[right]| — whichever is bigger has the bigger square.',
          'Place that square at the current write position, then move that pointer inward and the write position backward.',
          'Repeat until left and right meet — the result is built correctly without ever sorting.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [-4, -1, 0, 3, 10] }, { label: 'result', values: [null, null, null, null, null] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [4] } }, note: '|-4|=4 vs |10|=10 — 10 is bigger. Place 100 at the last slot.' },
            { snapshots: { result: [null, null, null, null, 100] }, highlights: { arr: { primary: [0], secondary: [3] } }, note: '|-4|=4 vs |3|=3 — 4 is bigger. Place 16 at the next slot from the back.' },
            { snapshots: { result: [null, null, null, 16, 100] }, highlights: { arr: { primary: [1], secondary: [3] } }, note: '|-1|=1 vs |3|=3 — 3 is bigger. Place 9.' },
            { snapshots: { result: [null, null, 9, 16, 100] }, highlights: { arr: { primary: [1], secondary: [2] } }, note: '|-1|=1 vs |0|=0 — 1 is bigger. Place 1.' },
            { snapshots: { result: [null, 1, 9, 16, 100] }, highlights: { arr: [2] }, note: 'Only index 2 (value 0) remains. Place 0. Final: [0, 1, 9, 16, 100] — built directly in order, no sort call.' },
            { snapshots: { result: [0, 1, 9, 16, 100] }, note: 'Done — result is fully sorted, built with a single pass.' },
          ],
        },
        code:
`public int[] sortedSquares(int[] arr) {
    int n = arr.length;
    int[] result = new int[n];
    int left = 0, right = n - 1;
    for (int write = n - 1; write >= 0; write--) {
        if (Math.abs(arr[left]) > Math.abs(arr[right])) {
            result[write] = arr[left] * arr[left];
            left++;
        } else {
            result[write] = arr[right] * arr[right];
            right--;
        }
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — for the result array' },
      },
    ],
  },
];

export default twoPointersDrills;
